import React, { useState, useEffect } from 'react';
import {
  Card,
  Title,
  Stack,
  Group,
  Button,
  Slider,
  Text,
  ActionIcon,
  Select,
  TextInput,
  NumberInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useUserRoleDistribution, useUpdateUserRoleDistribution } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';

type RoleEntry = { name: string; value: number; color: string };

// Module-level stable empty array — using `|| []` inline creates a new array on
// every render, which would re-trigger useEffect deps and cause an infinite loop
// when the query is still pending.
const EMPTY_ROLES: RoleEntry[] = [];

// Захардкоженные цвета для выбора
const getPredefinedColors = (t: (key: string) => string) => [
  { value: '#228be6', label: t('roleDistribution.colors.blue') },
  { value: '#40c057', label: t('roleDistribution.colors.green') },
  { value: '#fd7e14', label: t('roleDistribution.colors.orange') },
  { value: '#e03131', label: t('roleDistribution.colors.red') },
  { value: '#9775fa', label: t('roleDistribution.colors.purple') },
  { value: '#f59f00', label: t('roleDistribution.colors.yellow') },
  { value: '#0ca678', label: t('roleDistribution.colors.teal') },
  { value: '#fa5252', label: t('roleDistribution.colors.pink') },
  { value: '#868e96', label: t('roleDistribution.colors.gray') },
  { value: '#495057', label: t('roleDistribution.colors.darkGray') },
];

export const RoleDistributionSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: roleDistribution = EMPTY_ROLES, isLoading } = useUserRoleDistribution();
  const updateMutation = useUpdateUserRoleDistribution();

  const predefinedColors = getPredefinedColors(t);

  const [roles, setRoles] = useState(roleDistribution);

  useEffect(() => {
    setRoles(roleDistribution);
  }, [roleDistribution]);

  const handleAddRole = () => {
    if (roles.length >= 5) {
      showToast({
        title: t('common.error'),
        message: t('roleDistribution.maxRolesReached'),
        color: 'red',
      });
      return;
    }

    const newRole = {
      name: t('roleDistribution.roleName', {
        number: roles.length + 1,
      }),
      value: 0,
      color: predefinedColors[roles.length % predefinedColors.length].value,
    };
    setRoles([...roles, newRole]);
  };

  const handleRemoveRole = (index: number) => {
    setRoles(roles.filter((_, i) => i !== index));
  };

  const handleRoleChange = (index: number, field: string, value: string | number) => {
    const newRoles = [...roles];
    newRoles[index] = { ...newRoles[index], [field]: value };
    setRoles(newRoles);
  };

  const totalPercentage = roles.reduce((sum, role) => sum + role.value, 0);

  const handleSave = async () => {
    if (Math.abs(totalPercentage - 100) > 0.01) {
      showToast({
        title: t('common.error'),
        message: t('roleDistribution.percentageMustBe100'),
        color: 'red',
      });
      return;
    }

    try {
      await updateMutation.mutateAsync(roles);
      showToast({
        title: t('common.success'),
        message: t('roleDistribution.saved'),
        color: 'teal',
      });
    } catch (error) {
      showToast({
        title: t('common.error'),
        message: t('roleDistribution.saveError'),
        color: 'red',
      });
    }
  };

  if (isLoading) {
    return <Text>{t('roleDistribution.loading')}</Text>;
  }

  return (
    <Card withBorder radius="md" p="md">
      <Title order={4} mb="md">
        {t('roleDistribution.title')}
      </Title>

      <Stack gap="md">
        {roles.map((role, index) => (
          <Card key={index} withBorder p="sm">
            <Group align="flex-start" gap="md">
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group gap="md" align="flex-end">
                  <TextInput
                    label={t('roleDistribution.roleName')}
                    value={role.name}
                    onChange={(e) => handleRoleChange(index, 'name', e.target.value)}
                    size="xs"
                    style={{ flex: 1 }}
                  />

                  <Select
                    label={t('roleDistribution.color')}
                    value={role.color}
                    onChange={(color) => handleRoleChange(index, 'color', color || role.color)}
                    data={predefinedColors}
                    size="xs"
                    style={{ minWidth: 120 }}
                    renderOption={({ option }) => (
                      <Group gap="xs">
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 2,
                            backgroundColor: option.value,
                            border: '1px solid #ddd',
                          }}
                        />
                        <Text size="xs">{option.label}</Text>
                      </Group>
                    )}
                  />
                </Group>

                <Group gap="xs" align="center">
                  <Text size="xs" style={{ minWidth: 60 }}>
                    {t('roleDistribution.percentage')}
                  </Text>
                  <Slider
                    value={role.value}
                    onChange={(value) => handleRoleChange(index, 'value', value)}
                    min={0}
                    max={100}
                    step={1}
                    style={{ flex: 1 }}
                    size="xs"
                  />
                  <NumberInput
                    value={role.value}
                    onChange={(value) => handleRoleChange(index, 'value', value || 0)}
                    min={0}
                    max={100}
                    size="xs"
                    style={{ width: 60 }}
                  />
                </Group>
              </Stack>

              <ActionIcon
                color="red"
                variant="light"
                onClick={() => handleRemoveRole(index)}
                disabled={roles.length <= 1}
                title={t('roleDistribution.removeRole')}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Card>
        ))}

        <Group justify="space-between" align="center">
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={handleAddRole}
            disabled={roles.length >= 5}
          >
            {t('roleDistribution.addRole')}
          </Button>

          <Group gap="xs" align="center">
            <Text size="sm" c={Math.abs(totalPercentage - 100) > 0.01 ? 'red' : 'green'}>
              {t('roleDistribution.totalPercentage', { percentage: totalPercentage })}
            </Text>
            <Button
              onClick={handleSave}
              loading={updateMutation.isPending}
              disabled={Math.abs(totalPercentage - 100) > 0.01}
            >
              {t('roleDistribution.save')}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
};
